import { useMemo } from "react";
import PropTypes from "prop-types";
import { DataGrid } from "@mui/x-data-grid";
import { Box } from "@mui/material";
import AsyncDataGridExportProvider from "./AsyncDataGridExportProvider.jsx";
import AsyncDataGridPresetsProvider from "./dataGridPresets/AsyncDataGridPresetsProvider.jsx";

/**
 * Builds initialState for DataGrid, omitting facets controlled via URL when url persistence is enabled.
 *
 * @param {Object} params
 * @param {Object} [params.userInitialState]
 * @param {boolean} params.urlStateEnabled
 * @param {number} params.defaultPageSize
 * @param {boolean} params.isServerMode
 * @returns {Object}
 */
function buildDataGridInitialState({ userInitialState, urlStateEnabled, defaultPageSize, isServerMode }) {
  const base = userInitialState ?? {};

  if (urlStateEnabled) {
    const { columns, ...rest } = base;
    const nextColumns = columns ? { ...columns, columnVisibilityModel: undefined } : undefined;
    return {
      ...rest,
      ...(nextColumns ? { columns: nextColumns } : {}),
      density: base.density ?? (isServerMode ? undefined : "compact"),
    };
  }

  return {
    ...base,
    pagination: base.pagination ?? {
      paginationModel: { pageSize: defaultPageSize, page: 0 },
      ...(isServerMode ? { rowCount: 0 } : {}),
    },
    density: base.density ?? (isServerMode ? undefined : "compact"),
  };
}

/**
 * Shared DataGrid shell with export provider for client and server modes.
 *
 * @param {Object} props
 */
function AsyncDataGridView({
  gridWrapperSx,
  exportRows,
  exportLoading,
  exportConfig,
  gridColumns,
  isServerMode,
  hideExport,
  commonDataGridProps,
  userInitialState,
  defaultPageSize,
  dataGridModeProps,
  urlStateBindings,
  gridChangeHandlers = {},
  presetsBindings = null,
}) {
  const urlStateEnabled = Boolean(urlStateBindings?.enabled);

  const initialState = useMemo(
    () =>
      buildDataGridInitialState({
        userInitialState,
        urlStateEnabled,
        defaultPageSize,
        isServerMode,
      }),
    [userInitialState, urlStateEnabled, defaultPageSize, isServerMode],
  );

  const { initialState: _ignoredInitialState, ...restCommonProps } = commonDataGridProps; // eslint-disable-line no-unused-vars

  const urlControlledProps = urlStateEnabled
    ? {
        filterModel: urlStateBindings.filterModel,
        sortModel: urlStateBindings.sortModel,
        paginationModel: urlStateBindings.paginationModel,
        columnVisibilityModel: urlStateBindings.columnVisibilityModel,
        onFilterModelChange: urlStateBindings.onFilterModelChange,
        onSortModelChange: urlStateBindings.onSortModelChange,
        onPaginationModelChange: urlStateBindings.onPaginationModelChange,
        onColumnVisibilityModelChange: urlStateBindings.onColumnVisibilityModelChange,
      }
    : gridChangeHandlers;

  return (
    <Box sx={gridWrapperSx}>
      <AsyncDataGridPresetsProvider value={presetsBindings}>
        <AsyncDataGridExportProvider
          exportRows={exportRows}
          exportLoading={exportLoading}
          exportConfig={exportConfig}
          gridColumns={gridColumns}
          isServerMode={isServerMode}
          hideExport={hideExport}>
          <DataGrid {...restCommonProps} {...dataGridModeProps} {...urlControlledProps} initialState={initialState} />
        </AsyncDataGridExportProvider>
      </AsyncDataGridPresetsProvider>
    </Box>
  );
}

AsyncDataGridView.propTypes = {
  gridWrapperSx: PropTypes.object.isRequired,
  exportRows: PropTypes.array.isRequired,
  exportLoading: PropTypes.bool.isRequired,
  exportConfig: PropTypes.object.isRequired,
  gridColumns: PropTypes.array.isRequired,
  isServerMode: PropTypes.bool.isRequired,
  hideExport: PropTypes.bool.isRequired,
  commonDataGridProps: PropTypes.object.isRequired,
  userInitialState: PropTypes.object,
  defaultPageSize: PropTypes.number.isRequired,
  dataGridModeProps: PropTypes.object.isRequired,
  urlStateBindings: PropTypes.object,
  gridChangeHandlers: PropTypes.object,
  presetsBindings: PropTypes.object,
};

export default AsyncDataGridView;
